import { styled } from '@linaria/react';
import { Badge } from '../Badge';
import { fonts, layout, primitives, semantic, size, space } from '../../theme/tokens';
import { EntityImage } from '../EntityImage';

export const CardWrapper = styled.div<{
  $unavailable?: boolean;
  $selected?: boolean;
  $clickable?: boolean;
}>`
  display: flex;
  align-items: flex-start;
  gap: ${space['space-400']};
  padding: ${space['space-500']};
  background: ${({ $unavailable }) =>
    $unavailable ? semantic.bg.surfaceSubtle : semantic.bg.canvas};
  border: ${({ $selected }) =>
      $selected ? size.stroke['stroke-thick'] : size.stroke['stroke-default']}
    solid ${({ $selected }) => ($selected ? semantic.border.selected : semantic.border.secondary)};
  border-radius: ${size.radius['radius-lg']};
  overflow: hidden;
  opacity: ${({ $unavailable }) => ($unavailable ? 0.7 : 1)};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:has([data-card-select]:focus-visible) {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }

  & [data-card-select]:focus-visible {
    outline: none;
  }

  @media (max-width: 389px) {
    gap: ${space['space-200']};
    padding: ${space['space-400']};
  }
`;

export const Thumbnail = styled.div`
  width: 74px;
  height: 74px;
  border-radius: ${size.radius['radius-xs']};
  overflow: hidden;
  flex-shrink: 0;
  background: ${semantic.bg.surfaceSubtle};

  @media (max-width: 389px) {
    width: 56px;
    height: 56px;
  }

  @media (min-width: ${layout.desktopBreakpoint}) {
    width: 82px;
    height: 82px;
  }
`;

export const ThumbnailImage = styled(EntityImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const ThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: ${semantic.bg.surfaceSubtle};
`;

export const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-300']};
  flex: 1;
  min-width: 0;
`;

export const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${space['space-200']} ${space['space-600']};

  @media (max-width: 389px) {
    gap: ${space['space-200']} ${space['space-300']};
  }
`;

export const Name = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
  line-height: ${primitives.typography.lineHeight.normal};
  letter-spacing: ${primitives.typography.tracking.normal};
  color: ${semantic.text.primary};
  flex: 1;
  min-width: 80px;
  overflow-wrap: break-word;

  @media (min-width: ${layout.desktopBreakpoint}) {
    font-size: ${fonts.size.base};
  }
`;

export const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  flex-shrink: 0;
  text-align: right;
`;

export const SecondaryPrice = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  line-height: ${primitives.typography.lineHeight.normal};
  letter-spacing: ${primitives.typography.tracking.normal};
  color: ${semantic.text.primary};

  @media (min-width: ${layout.desktopBreakpoint}) {
    font-size: ${fonts.size.sm};
  }
`;

export const BottomRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${space['space-200']} ${space['space-300']};
`;

export const DetailsButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  font-weight: ${fonts.weight.medium};
  line-height: ${primitives.typography.lineHeight.normal};
  letter-spacing: ${primitives.typography.tracking.normal};
  color: ${semantic.text.primary};
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }

  @media (min-width: ${layout.desktopBreakpoint}) {
    font-size: ${fonts.size.sm};
  }
`;

export const PromotionPill = styled(Badge)`
  background: ${semantic.bg.brand};
  color: ${semantic.text.inverse};
  padding: ${space['space-100']} ${space['space-300']};
  border-radius: ${size.radius['radius-full']};
  font-weight: ${fonts.weight.medium};
  letter-spacing: normal;
  white-space: nowrap;
`;

export const UnavailableText = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
  color: ${semantic.text.secondary};
`;
